// src/components/Contact.jsx
import { useState } from "react";
import {
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaEnvelope,
  FaRegQuestionCircle,
} from "react-icons/fa";
import axios from "axios";

const API_URL = "https://bcm-backend-9ca9.onrender.com";

export default function Contact() {
  const [hideInfo, setHideInfo] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess(false);
    setError(false);

    if (!formData.name || !formData.email || !formData.message) {
      alert("Please fill all fields.");
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API_URL}/messages`, formData);
      setSuccess(true);
      setFormData({ name: "", email: "", message: "" });
    } catch (err) {
      console.error("Failed to submit contact form:", err);
      setError(true);
    } finally {
      setSubmitting(false);
      setTimeout(() => {
        setSuccess(false);
        setError(false);
      }, 5000);
    }
  };

  return (
    <section id="contact" className="relative py-20 px-6 md:px-10 text-white overflow-hidden">
      {/* Google Map Background */}
      <div className="absolute inset-0 z-0">
        <iframe
          title="Hostel Location"
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1561.6991154555337!2d74.80265961474052!3d12.984925972327604!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba351ed3b843127%3A0x337976f31d40f04c!2sBCM%20Hostel!5e1!3m2!1sen!2sin!4v1754532446077!5m2!1sen!2sin"
          className="w-full h-full"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
        ></iframe>
      </div>

      {/* Contact Info and Form */}
      {!hideInfo && (
        <div className="relative z-10 max-w-5xl mx-auto text-center bg-black/60 p-6 sm:p-10 rounded-xl backdrop-blur-md">
          <h2 className="text-3xl md:text-4xl font-bold text-yellow-300 mb-4">
            Contact Information
          </h2>

          <p className="text-lg text-yellow-100 mb-10 flex items-center justify-center gap-2">
            <FaRegQuestionCircle className="text-yellow-200" />
            Have questions? Reach out to us!
          </p>

          {/* Info Cards */}
          <div className="grid gap-6 md:gap-8 sm:grid-cols-2 md:grid-cols-3 mb-10">
            <InfoCard
              icon={<FaMapMarkerAlt className="text-maroon text-3xl" />}
              title="Address"
              content={
                <>
                  BCWD Hostel Surathkal,
                  <br />
                  Near Surathkal Police Station,
                  <br />
                  Surathkal, Karnataka – 575014
                </>
              }
            />
            <InfoCard
              icon={<FaEnvelope className="text-maroon text-3xl" />}
              title="Email"
              content={
                <a
                  href="mailto:bcwdhostel.surathkal@gov.in"
                  className="hover:underline break-all text-sm mt-1"
                >
                  bcwdhostel.surathkal@gov.in
                </a>
              }
            />
            <InfoCard
              icon={<FaPhoneAlt className="text-maroon text-3xl" />}
              title="Phone"
              content={
                <a href="tel:+919876500000" className="hover:underline text-sm mt-1">
                  +91 98765 00000
                </a>
              }
            />
          </div>

          {/* Contact Form */}
          <form onSubmit={handleSubmit} className="max-w-2xl mx-auto grid gap-6 text-left">
            <h3 className="text-2xl font-semibold text-yellow-200 mb-2 text-center">
              Send Us a Message
            </h3>

            <InputField
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Your Name"
            />
            <InputField
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Your Email"
            />
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows="4"
              placeholder="Your Message"
              className="w-full p-3 rounded-md bg-white/80 text-maroon placeholder-maroon outline-none focus:ring-2 focus:ring-yellow-300"
            ></textarea>

            <button
              type="submit"
              disabled={submitting}
              className={`w-full font-semibold py-3 rounded-md transition ${
                submitting
                  ? "bg-yellow-300 opacity-70 cursor-not-allowed"
                  : "bg-yellow-300 hover:bg-yellow-400 text-maroon"
              }`}
            >
              {submitting ? "Sending..." : "Submit Message"}
            </button>

            {success && (
              <p className="text-green-500 font-semibold text-center mt-3">
                ✅ Your message has been sent!
              </p>
            )}
            {error && (
              <p className="text-red-500 font-semibold text-center mt-3">
                ❌ Submission failed. Please try again.
              </p>
            )}
          </form>
        </div>
      )}

      {/* Mobile View Map Button */}
      {!hideInfo && (
        <button
          onClick={() => setHideInfo(true)}
          className="absolute bottom-6 right-6 bg-yellow-300 text-maroon px-4 py-2 rounded-lg font-semibold shadow-md hover:bg-yellow-400 transition md:hidden"
        >
          View Full Map
        </button>
      )}
    </section>
  );
}

// Small Reusable Components
function InfoCard({ icon, title, content }) {
  return (
    <div className="flex flex-col items-center text-center sm:text-left sm:items-start gap-3 p-6 bg-white/90 text-maroon rounded-xl hover:shadow-lg transition transform duration-300">
      {icon}
      <div>
        <h4 className="font-semibold">{title}</h4>
        <p className="text-sm mt-1">{content}</p>
      </div>
    </div>
  );
}

function InputField({ type, name, value, onChange, placeholder }) {
  return (
    <input
      type={type}
      name={name}
      autoComplete={name}
      value={value}
      onChange={onChange}
      required
      placeholder={placeholder}
      className="w-full p-3 rounded-md bg-white/80 text-maroon placeholder-maroon outline-none focus:ring-2 focus:ring-yellow-300"
    />
  );
}
